import { useState, useEffect, useCallback } from 'react';
import {
  getReferrerNames,
  getAllReferrerNames,
  createReferrerName,
  updateReferrerName,
  deleteReferrerName,
} from '../services/ReferrerNameService';
import { ReferrerName } from '../types/interfaces';

let referrerNamesRequest: Promise<{ success?: boolean; data?: ReferrerName[]; message?: string }> | null = null;
let referrerNamesRequestIncludesDisabled: boolean | null = null;

export const useReferrerNames = (includeDisabled = false) => {
  const [referrerNames, setReferrerNames] = useState<ReferrerName[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const fetchReferrerNames = useCallback(async (forceRefresh = false) => {
    if (
      !forceRefresh &&
      referrerNamesRequest &&
      referrerNamesRequestIncludesDisabled === includeDisabled
    ) {
      try {
        const response = await referrerNamesRequest;
        if (response.success && response.data) setReferrerNames(response.data);
        else if (response.message) setError(response.message);
      } catch {
        setError('Error al cargar los referidos');
      } finally {
        setIsLoading(false);
      }
      return;
    }
    if (forceRefresh) {
      referrerNamesRequest = null;
      referrerNamesRequestIncludesDisabled = null;
    }
    referrerNamesRequest = includeDisabled ? getAllReferrerNames() : getReferrerNames();
    referrerNamesRequestIncludesDisabled = includeDisabled;
    setIsLoading(true);
    setError(null);
    try {
      const response = await referrerNamesRequest;
      if (response.success && response.data) {
        setReferrerNames(response.data);
      } else {
        const errorMsg = response.message || 'Error al cargar los referidos';
        setError(errorMsg);
      }
    } catch (err) {
      setError('Error al cargar los referidos');
    } finally {
      referrerNamesRequest = null;
      referrerNamesRequestIncludesDisabled = null;
      setIsLoading(false);
    }
  }, [includeDisabled]);

  useEffect(() => {
    fetchReferrerNames();
  }, [fetchReferrerNames]);

  const refreshReferrerNames = useCallback(() => {
    return fetchReferrerNames(true);
  }, [fetchReferrerNames]);

  const createReferrer = useCallback(
    async (
      name: string,
      code: string,
      cuit?: string | null,
      commissionPercentage?: number | null,
    ): Promise<{ success: boolean; message?: string; status?: number; code?: string }> => {
      setIsCreating(true);
      setError(null);
      try {
        const response = await createReferrerName(name, code, cuit, commissionPercentage);
        if (response.success) {
          await refreshReferrerNames();
          return { success: true };
        }
        const errorMsg = response.message || 'Error al crear el referido';
        setError(errorMsg);
        return { success: false, message: errorMsg, status: response.status };
      } catch (err) {
        const errorMsg = 'Error al crear el referido';
        setError(errorMsg);
        return { success: false, message: errorMsg };
      } finally {
        setIsCreating(false);
      }
    },
    [refreshReferrerNames],
  );

  const updateReferrer = useCallback(
    async (
      id: number,
      name: string,
      code: string,
      cuit?: string | null,
      commissionPercentage?: number | null,
      enabled?: boolean,
    ): Promise<{ success: boolean; message?: string }> => {
      setIsUpdating(true);
      setError(null);
      try {
        const response = await updateReferrerName(id, name, code, cuit, commissionPercentage, enabled);
        if (response.success) {
          await refreshReferrerNames();
          return { success: true };
        }
        const errorMsg = response.message || 'Error al actualizar el referido';
        setError(errorMsg);
        return { success: false, message: errorMsg };
      } catch (err) {
        const errorMsg = 'Error al actualizar el referido';
        setError(errorMsg);
        return { success: false, message: errorMsg };
      } finally {
        setIsUpdating(false);
      }
    },
    [refreshReferrerNames],
  );

  const removeReferrer = useCallback(
    async (id: number): Promise<{ success: boolean; message?: string }> => {
      setIsDeleting(true);
      setError(null);
      try {
        const response = await deleteReferrerName(id);
        if (response.success) {
          await refreshReferrerNames();
          return { success: true };
        }
        const errorMsg = response.message || 'Error al dar de baja el referido';
        setError(errorMsg);
        return { success: false, message: errorMsg };
      } catch (err) {
        const errorMsg = 'Error al dar de baja el referido';
        setError(errorMsg);
        return { success: false, message: errorMsg };
      } finally {
        setIsDeleting(false);
      }
    },
    [refreshReferrerNames],
  );

  return {
    referrerNames,
    isLoading,
    error,
    isCreating,
    isUpdating,
    isDeleting,
    refreshReferrerNames,
    createReferrer,
    updateReferrer,
    removeReferrer,
  };
};
